import { useOutsideClick } from "hooks/useOutsideClick";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  sendFileMessageStart,
  sendTextMessageStart,
  setTypingWord,
  setActingMessage,
  editMessageStart,
} from "../../../redux/chats/chats.actions";
import Filepreview from "./components/Filepreview";
import Pretexts from "./components/Pretexts/index.js";
import useMediaQuery from "use-mediaquery";
import { ThreeDots } from "react-loader-spinner";
import { useDropzone } from "react-dropzone";
import useTranslation from "hooks/useTranslation";
import { truncate } from "utils/text";
import {
  ReplyBox,
  EditBox,
  CloseOutlinedIcon,
  SmilingComponent,
  AttachComponent,
  NewSendIcon,
  ChevronDownIcon,
  BookmarkIconComponent,
} from "assets/icons";
import { verifyFileToOpen, isFileSizeValid } from "utils/validation.js";
import VoiceRecorder from "./components/VoiceRecorder";
import UserFormInfoModal from "./components/UserFormInfoModal";
import styles from "./styles.module.scss";
import { WarningOutlineComponent } from "assets/icons";
import { EMOJIS } from "constants/emojis";
import { clsx } from "clsx";
import PendingConvFooter from "pages/conversation/components/Chat/PendingConvFooter";
import usePasteHandler from "hooks/usePasteHandler";

const { getNotifMsg } = require("utils/message");

const getMaxValidCharsCount = ({ chatsData }) =>
  chatsData.maxValidCharactersCount;
const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;
const getTypingWord = ({ chatsData }) => chatsData.typingWord;
const getActingMessage = ({ chatsData }) => chatsData.messageUnderAction;
const getVoiceRecording = ({ voiceMsgData }) => voiceMsgData.voiceRecording;
const getContainer = ({ userData }) => userData.container.data;

const ALLOWED_PACKAGES_FOR_VOICE_FEAT = ["premium", "trial", "premiumPlus"];

const USER_FORM_INIT_V = {
  show: false,
  payload: {
    name: "",
    email: "",
    phone: "",
    info: [],
  },
};

// sizes are in MB
const maxUploadFileSizePerPackage = {
  commerical: 20,
  professional: 10,
  growth: 20,
  premium: 30,
  premiumPlus: 100,
  vip: 100,
  trial: 30,
};

const ConversationsFooter = () => {
  const [box, setBox] = useState({ content: "", show: false });
  const [textMessage, setTextMessage] = useState("");
  const [fileMessage, setFileMessage] = useState(null);
  const [showUserFormInfoModal, setShowUserFormInfoModal] =
    useState(USER_FORM_INIT_V);

  const maxValidCharsCount = useSelector(getMaxValidCharsCount);
  const selectedConversation = useSelector(getSelectedConversation);
  const typingWord = useSelector(getTypingWord);
  const actingMessage = useSelector(getActingMessage);
  const voiceRecording = useSelector(getVoiceRecording);
  const container = useSelector(getContainer);
  usePasteHandler((file) => openFilepreview(file));

  const showVoiceMsgFeature = ALLOWED_PACKAGES_FOR_VOICE_FEAT.includes(
    container?.package?.type
  );

  const showExceedCharsCountBar = textMessage.length > maxValidCharsCount;
  const convType = selectedConversation?.conversation?.convType;
  const showPendingFooter =
    ["closed", "pending", "otherConvs"].includes(convType) &&
    !selectedConversation?.chatWithVisitor;

  const isSendBtnDisabled = showExceedCharsCountBar || !textMessage?.trim();
  const isSmallDevice = useMediaQuery("(max-width: 600px)");
  const chatInputRef = useRef();
  const cannedMsgBtnRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const wrapperRef = useRef(null);
  const { getTranslation, getAppLocale } = useTranslation("CHAT");
  const { t } = getTranslation();

  const locale = getAppLocale();

  const truncedTypingWords = truncate(typingWord, 250);

  const showTypingWordPreviewSection = typingWord;

  const showUserInfoBtnTrigger = !!selectedConversation?.user?.info?.length;

  const onDrop = useCallback((acceptedFiles) => {
    openFilepreview(acceptedFiles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  const dispatch = useDispatch();

  const handleToggleMessages = (content, e) => {
    // just change the content when the other toggle btn is pressed while the box is shown
    if (box.show && box.content !== content) {
      setBox({ ...box, content });
      return;
    }
    setBox({ ...box, content, show: !box.show });
  };

  const handleTypeMessage = (value) => {
    if (value === "/") {
      handleToggleMessages("messages");
    }
    setTextMessage(value);
    if (box.show) {
      setBox({ ...box, show: false });
    }
  };

  const handleSendTextMessage = () => {
    dispatch(
      sendTextMessageStart({
        conversationId: selectedConversation.conversation.id,
        clientId: selectedConversation.user.id,
        message: textMessage,
        chatWithVisitor: selectedConversation.chatWithVisitor ? true : false,
        chatWithPartner: selectedConversation.chatWithPartner ? true : false,
      })
    );
    setBox({ ...box, show: false });
    setTextMessage("");
  };

  const handleKeyDown = (e) => {
    if (!e || (e && e.key === "Enter" && !e.shiftKey)) {
      e.preventDefault();

      handleSendTextMessageButtonClick();
    }
  };

  const handleEditTextMessage = () => {
    dispatch(
      editMessageStart({
        message: textMessage,
        messageId: actingMessage.id,
        conversationId: selectedConversation.conversation.id,
      })
    );
    setBox({ ...box, show: false });
    setTextMessage("");
  };

  const openFilepreview = (file) => {
    if (!file) return;

    const maxSize = maxUploadFileSizePerPackage[container.package.type];
    const isValidSize = maxSize && isFileSizeValid(file?.size, maxSize);

    if (!isValidSize) {
      toast.info(getNotifMsg("FILE_SIZE_MORE_THAN_MAX", locale));
      return;
    }

    let selectedFile = verifyFileToOpen(file);
    setFileMessage(selectedFile);
  };

  const handleSendFileMessage = () => {
    dispatch(
      sendFileMessageStart({
        conversationId: selectedConversation.conversation.id,
        clientId: selectedConversation.user.id,
        file: fileMessage.file,
        size: fileMessage.size,
      })
    );
    setFileMessage(null);
  };

  const doesContainTarget = (target, element) => {
    return element.contains(target);
  };

  const handleClickOutsideTheBox = (e) => {
    const { target } = e;
    if (
      box.show &&
      !doesContainTarget(target, cannedMsgBtnRef.current) &&
      !doesContainTarget(target, emojiBtnRef.current)
    ) {
      setBox({ ...box, show: false });
    }
  };

  useOutsideClick(wrapperRef, handleClickOutsideTheBox);

  const handleClearActingMessage = () => {
    dispatch(setActingMessage(null));
    if (actingMessage?.actionType === "EDIT") setTextMessage("");
  };

  const handleScrollIntoRepliedMsg = () => {
    const element = document.querySelector(`[data-id="${actingMessage?.id}"]`);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "end" });

      // add animation
      element.classList.remove("animate-replied-msg");
      void element.offsetWidth;
      element.classList.add("animate-replied-msg");
    }
  };

  const handleSendTextMessageButtonClick = () => {
    if (isSendBtnDisabled) return;

    if (actingMessage?.actionType === "EDIT") handleEditTextMessage();
    else handleSendTextMessage();

    chatInputRef.current.style.height = "auto";
  };

  function handleUserInfoClick() {
    setShowUserFormInfoModal({
      show: true,
      payload: {
        name: selectedConversation?.user?.name,
        email: selectedConversation?.user?.email,
        phone: selectedConversation?.user?.phone,
        info: selectedConversation?.user?.info,
      },
    });
  }

  const handleScrollDown = () => {
    const convBodyEl = document.querySelector("#convBodyEl");
    convBodyEl.scrollTop = convBodyEl.scrollHeight;
  };

  const handleChangeFileInput = (e) => {
    openFilepreview(e.target.files[0]);

    // making sending the same file possible
    e.target.value = "";
  };

  const renderCharacterExceedWarningBar = (
    <div className={styles.character_exceed_msg_bar}>
      <div className={styles.character_exceed_msg_bar__inner}>
        <WarningOutlineComponent />
        <span>مقدار ارسالی بیش از {maxValidCharsCount} کاراکتر می باشد</span>
      </div>
    </div>
  );

  const handleInput = () => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = "auto";
      chatInputRef.current.style.height = `${chatInputRef.current.scrollHeight}px`;
    }
  };

  const renderMessagePreview = (
    <div className="conv__body__footer__msg_preview">
      <div className="flex flex-ai-c">
        <div className="conv__body__footer__msg_preview__icon">
          <img
            src={actingMessage?.actionType === "REPLY" ? ReplyBox : EditBox}
            alt={actingMessage?.actionType === "REPLY" ? "reply" : "edit"}
          />
        </div>
        <div className="conv__body__footer__msg_preview__body flex flex-column">
          <span className="conv__body__footer__msg_preview__body__tilte">
            {actingMessage?.actionType === "REPLY" ? t("REPLAY") : t("EDITING")}
          </span>
          <span
            onClick={handleScrollIntoRepliedMsg}
            className="conv__body__footer__msg_preview__body__text"
          >
            {truncate(actingMessage?.content, 150)}
          </span>
        </div>
      </div>
      <div className="conv__body__footer__msg_preview__close">
        <img
          onClick={handleClearActingMessage}
          src={CloseOutlinedIcon}
          alt="close"
        />
      </div>
    </div>
  );

  const renderTypingPreviewSection = showTypingWordPreviewSection && (
    <span className="flex flex-ai-c conv__body__footer__typing__word">
      <span>
        {truncedTypingWords}
        <ThreeDots
          height="30"
          width="30"
          radius="9"
          color="#b10e84"
          ariaLabel="three-dots-loading"
          wrapperStyle={{
            display: "inline-block",
            position: "absolute",
            marginRight: "4px",
          }}
          wrapperClass={styles.threedots_typing_loader}
          visible={true}
        />
      </span>
    </span>
  );

  const renderInfoBtnTrigger = showUserInfoBtnTrigger && (
    <span
      className="user_form_info"
      data-hide={!!actingMessage || !!box?.show}
      onClick={handleUserInfoClick}
    >
      {t("USER_FORM_INFO")}
    </span>
  );

  const renderScrollDownChevron = (
    <div
      className="scroll-down display-none"
      id="scroll-down"
      onClick={handleScrollDown}
    >
      <ChevronDownIcon />
    </div>
  );

  const handleSelectEmoji = (emoji) => {
    setTextMessage(textMessage ? textMessage + emoji : "" + emoji);
    chatInputRef.current.focus();
  };

  const renderEmojis = () =>
    EMOJIS.map((emoji, index) => {
      return (
        <span
          className="conv__body__footer__emoji"
          onClick={() => handleSelectEmoji(emoji)}
          key={index}
        >
          {emoji}
        </span>
      );
    });

  const renderBox = () => {
    const { content } = box;

    if (content === "messages") {
      return (
        <Pretexts
          emojis={EMOJIS}
          handleTypeMessage={handleTypeMessage}
          chatInputRef={chatInputRef}
        />
      );
    } else if (content === "emojis") {
      return (
        <div className="conv__body__footer__emoji__container">
          {renderEmojis()}
        </div>
      );
    }
  };

  const renderInputOptionsCollection = (
    <>
      <input onChange={handleChangeFileInput} id="test" type="file" />

      {showVoiceMsgFeature && <VoiceRecorder />}

      <div
        onClick={() => {
          handleToggleMessages("emojis");
        }}
        ref={emojiBtnRef}
        className="conv__body__footer__actions__icon__emoji flex flex-ai-c flex-jc-c"
      >
        <SmilingComponent />
      </div>

      <label htmlFor="test">
        <div className="conv__body__footer__actions__icon__attach flex flex-ai-c flex-jc-c">
          <AttachComponent />
        </div>
      </label>
    </>
  );

  useEffect(() => {
    dispatch(
      setTypingWord({
        conversationId: null,
        msg: null,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    selectedConversation.conversation && selectedConversation.conversation.id,
  ]);

  useEffect(() => {
    if (actingMessage) {
      // also check content type that should be string
      if (actingMessage?.actionType === "EDIT")
        setTextMessage(actingMessage.content);
      chatInputRef.current.focus();
    }
  }, [actingMessage]);

  if (showPendingFooter) {
    return <PendingConvFooter convType={convType} />;
  }

  return (
    <div className="conv__body__footer flex flex-column flex-jc-e">
      <div className="conv__body__footer__dropzone" {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive && (
          <div className="conv__body__footer__dropzone__active">
            {t("DROP_FILE_HERE")}
          </div>
        )}
      </div>

      <div className={styles.preview_section_container}>
        <div>
          {renderTypingPreviewSection}
          {renderInfoBtnTrigger}
        </div>
        {renderScrollDownChevron}
      </div>

      {/* acting message preveiw */}
      {actingMessage && renderMessagePreview}
      {showExceedCharsCountBar && renderCharacterExceedWarningBar}
      <div
        data-recording={voiceRecording}
        className="conv__body__footer__input flex flex-ai-c flex-jc-c"
      >
        <Filepreview
          handleSendFileMessage={handleSendFileMessage}
          setFileMessage={setFileMessage}
          showFileModal={fileMessage ? true : false}
          fileMessage={fileMessage}
        />

        {showUserFormInfoModal.show && (
          <UserFormInfoModal
            setShowUserFormInfoModal={setShowUserFormInfoModal}
            showUserFormInfoModal={showUserFormInfoModal}
            USER_FORM_INIT_V={USER_FORM_INIT_V}
            data={showUserFormInfoModal.payload}
          />
        )}

        <div
          ref={wrapperRef}
          className={clsx(
            "conv__body__footer__cannedmsg_box",
            box.show && "conv__body__footer__cannedmsg_box__visible"
          )}
        >
          {renderBox()}
        </div>

        <div
          ref={cannedMsgBtnRef}
          className="conv__body__footer__cannedmsg flex flex-ai-c flex-jc-c"
        >
          <div
            className="conv__body__footer__cannedmsg__icon flex flex-ai-c flex-jc-c"
            onClick={(e) => {
              handleToggleMessages("messages", e);
            }}
          >
            <BookmarkIconComponent />
          </div>
        </div>

        <div className={styles.input_and_options}>
          <textarea
            ref={chatInputRef}
            className="conv__body__footer__editor_wrap__editor conv__body__footer__editor_wrap"
            placeholder={
              isSmallDevice ? t("CHAT_INPUT_SHORT_PH") : t("CHAT_INPUT_PH")
            }
            onChange={(e) => handleTypeMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            value={textMessage}
            rows="1"
            autoFocus
          />

          <div className={styles.input_options_collection}>
            <div className="conv__body__footer__actions flex flex-ai-c flex-jc-sb">
              {renderInputOptionsCollection}
            </div>
            <div className="conv__body__footer__sendbtn flex flex-ai-c flex-jc-c">
              <button
                onClick={handleSendTextMessageButtonClick}
                className="btn btn-primary btn-small send_btn"
                disabled={isSendBtnDisabled}
              >
                <span>{t("SEND")}</span>

                <img src={NewSendIcon} className="Send_Icon" alt="Send_Icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationsFooter;
